import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

import { auth } from "../../firebase-config";          // <-- MAIN APP AUTH
import { authPortal } from "../../firebase-portal";    // <-- PORTAL AUTH (magic link)
import { useActAsClient } from "./ActAsClientContext";

export default function RequireClient({ children }) {
  const [params] = useSearchParams();
  const actAs = useActAsClient();

  const actAsClientParam = useMemo(
    () => params.get("actAsClient") || params.get("as"),
    [params]
  );

  const shouldUseStaffAuth = Boolean(actAsClientParam || actAs?.isActingAs);

  const [staffUser, setStaffUser] = useState(null);
  const [portalUser, setPortalUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [authStatus, setAuthStatus] = useState("checking"); // checking | authorized | unauthorized

  // Listen to BOTH auth instances, but only use the one we need
  useEffect(() => {
    let unsubStaff = () => {};
    let unsubPortal = () => {};

    unsubStaff = onAuthStateChanged(auth, (u) => {
      setStaffUser(u);
      // don't flip loading off yet; portal listener may also be needed
    });

    unsubPortal = onAuthStateChanged(authPortal, (u) => {
      setPortalUser(u);
    });

    // simple settle: give both listeners a tick
    const t = setTimeout(() => setLoading(false), 0);

    return () => {
      clearTimeout(t);
      unsubStaff();
      unsubPortal();
    };
  }, []);

  useEffect(() => {
    if (loading) return;

    (async () => {
      try {
        if (shouldUseStaffAuth) {
          if (!staffUser) {
            setAuthStatus("unauthorized");
            return;
          }

          const idTokenResult = await staffUser.getIdTokenResult(true);
          const claims = idTokenResult?.claims || {};
          const roles = Array.isArray(claims.roles) ? claims.roles : [];

          // Your one-time rule: must be an admin (not org-specific)
          if (roles.includes("admin")) {
            setAuthStatus("authorized");
            return;
          }

          setAuthStatus("unauthorized");
          return;
        }

        // No act-as => real client flow must be portal auth
        if (!portalUser) {
          setAuthStatus("unauthorized");
          return;
        }

        const idTokenResult = await portalUser.getIdTokenResult(true);
        const claims = idTokenResult?.claims || {};
        const role = claims.role;

        if (role === "client") {
          setAuthStatus("authorized");
          return;
        }

        setAuthStatus("unauthorized");
      } catch (err) {
        console.error("[RequireClient] token/claims error:", err);
        setAuthStatus("unauthorized");
      }
    })();
  }, [loading, shouldUseStaffAuth, staffUser, portalUser]);

  if (authStatus === "checking") {
    return <div style={{ padding: 32 }}>Verifying session…</div>;
  }

  if (authStatus === "authorized") {
    return children;
  }

  return <Navigate to="/portal/invite-invalid" replace />;
}